// Product Visual System — Design tokens & category-specific personality
// Shared structural rules with per-category visual identity

// ─── Structural Constants ──────────────────────────
export const CARD_SYSTEM = {
  // Image area
  imageAspect: 'aspect-[3/4]',         // Portrait-leaning, ideal for digital products
  imageHeight: 'h-[300px] md:h-[320px]', // Fixed height alternative
  borderRadius: 'rounded-xl',
  imageBorderRadius: 'rounded-t-xl',

  // Card spacing
  padding: 'p-5 md:p-6',
  gap: 'gap-6 md:gap-7',

  // Grid
  gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',

  // Hover
  hoverLift: 'hover:-translate-y-2',
  hoverShadow: 'hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]',
  transition: 'transition-all duration-500',

  // Badge position
  badgePosition: 'absolute top-4 left-4',
  labelPosition: 'absolute top-4 right-4',
};

// ─── Typography Hierarchy ──────────────────────────
export const CARD_TYPOGRAPHY = {
  title: "font-bold text-[#4A3525] text-base leading-tight",
  titleFont: "'Playfair Display', serif",
  subtitle: "text-sm text-[#8A7D72]",
  meta: "text-[11px] text-[#A89B8E]",
  price: "text-sm font-semibold text-[#4A3525]",
  badge: "text-[9px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 shadow-sm",
  categoryPill: "text-[10px] font-medium px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm border border-white/30 shadow-sm tracking-wide",
};

// ─── Category Visual Identity ──────────────────────
// Each category has its own accent palette and mood
// These are used for image backgrounds, pill colors, and subtle tinting
export const CATEGORY_IDENTITY = {
  Business: {
    bg: '#F0F2F5',           // Cool steel gray
    accent: '#5B6B8A',       // Slate blue
    pillText: '#5B6B8A',
    mood: 'professional',
  },
  Baby: {
    bg: '#FFF5F8',           // Soft blush pink
    accent: '#E8A0BF',       // Rose
    pillText: '#C4789E',
    mood: 'tender',
  },
  Wedding: {
    bg: '#FFF8F2',           // Warm ivory
    accent: '#C8A882',       // Gold taupe
    pillText: '#A8896E',
    mood: 'romantic',
  },
  Travel: {
    bg: '#EEF6F4',           // Aqua mist
    accent: '#4A9E8E',       // Teal
    pillText: '#3A8070',
    mood: 'adventurous',
  },
  Finance: {
    bg: '#F0F4FA',           // Ice blue
    accent: '#4A6FA5',       // Navy
    pillText: '#4A6FA5',
    mood: 'trustworthy',
  },
  Health: {
    bg: '#F5F0F8',           // Soft lavender
    accent: '#8B6AAE',       // Purple
    pillText: '#7A5A9E',
    mood: 'calming',
  },
  Hobby: {
    bg: '#F8F3EB',           // Warm parchment
    accent: '#8A6F52',       // Warm brown
    pillText: '#6B5A42',
    mood: 'cozy',
  },
  Home: {
    bg: '#F2F5EE',           // Sage whisper
    accent: '#6B8A5E',       // Forest green
    pillText: '#5A7A4E',
    mood: 'homey',
  },
  Food: {
    bg: '#FEF6F0',           // Warm cream
    accent: '#C77849',       // Terracotta
    pillText: '#B06838',
    mood: 'appetizing',
  },
  Studies: {
    bg: '#F5F0E8',           // Aged paper
    accent: '#6B4C3B',       // Sepia
    pillText: '#5A3C2B',
    mood: 'academic',
  },
  Seasonal: {
    bg: '#FDF5F5',           // Holly red tint
    accent: '#C41E3A',       // Rich red
    pillText: '#A01830',
    mood: 'festive',
  },
  ADHD: {
    bg: '#FFF8E0',           // Sunny yellow
    accent: '#FF6B35',       // Energetic orange
    pillText: '#E05A28',
    mood: 'energetic',
  },
  Pets: {
    bg: '#FFF8E1',           // Warm gold
    accent: '#D4883E',       // Honey
    pillText: '#B87030',
    mood: 'playful',
  },
  Relationships: {
    bg: '#F8F0F4',           // Soft plum
    accent: '#C4728A',       // Dusty rose
    pillText: '#A85A72',
    mood: 'intimate',
  },
  Everyday: {
    bg: '#FFF5E5',           // Warm peach
    accent: '#D4883E',       // Amber
    pillText: '#B87030',
    mood: 'practical',
  },
  Productivity: {
    bg: '#FAF5F0',           // Warm beige
    accent: '#C49A6C',       // Caramel
    pillText: '#A88050',
    mood: 'focused',
  },
};

// ─── Badge Styles (shared) ─────────────────────────
export const BADGE_STYLES = {
  'Best sor': { text: 'BESTSELLER', cls: 'bg-white/90 backdrop-blur-sm text-[#4A3525] border border-[#DDD4C8] rounded-full' },
  'Trending':    { text: 'TRENDING',   cls: 'bg-white/90 backdrop-blur-sm text-[#4A3525] border border-[#DDD4C8] rounded-full' },
  'Hot':         { text: 'HOT',        cls: 'bg-[#4A3525] text-white rounded-full' },
  'New':         { text: 'NEW',        cls: 'bg-white/90 backdrop-blur-sm text-[#4A3525] border border-[#DDD4C8] rounded-full' },
  'Best value':  { text: 'BEST VALUE', cls: 'bg-[#4A3525] text-white rounded-full' },
  'High demand': { text: 'POPULAR',    cls: 'bg-white/90 backdrop-blur-sm text-[#4A3525] border border-[#DDD4C8] rounded-full' },
  'Seasonal':    { text: 'SEASONAL',   cls: 'bg-white/90 backdrop-blur-sm text-[#8A7D72] border border-[#DDD4C8] rounded-full' },
};

// Get category identity with fallback
export const getCategoryIdentity = (category) => {
  return CATEGORY_IDENTITY[category] || CATEGORY_IDENTITY.Productivity;
};
